// 我的帖子页
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Empty, Form, Layout, Select, Skeleton, Tag } from 'antd';
import { ClockCircleOutlined, EnvironmentOutlined, LikeOutlined, MessageOutlined, PlusOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import PageTopbar from '@/components/pagetopbar';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loadMyPosts } from '@/store/modules/posts';
import PostStatus from '@/components/poststatus';
import PullToRefresh from '@/components/pulltorefresh';
import { useBrowseState } from '@/utils/browseState';
import usePageBack from '@/utils/usePageBack';
import PostSchoolBinding from '@/components/postschoolbinding';
import PostBadges from '@/components/postbadges';
import './index.scss';

const { Content } = Layout;

const formatDateTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('zh-CN', {
    month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

const MyPostCard = ({ post }) => {
  const createdAt = formatDateTime(post.created_at);
  const expireAt = formatDateTime(post.expire_time);

  return (
    <article className="myPosts-card" data-post-id={post.id}>
      <Link className="myPosts-card-link" to={`/posts/${post.id}`} aria-label={`查看帖子：${post.title}`}>
      <div className="myPosts-card-top">
        <PostStatus post={post} className="myPosts-status" />
        <PostBadges post={post} />
        {createdAt && <time dateTime={post.created_at}>{createdAt} 发布</time>}
      </div>
      <h2>{post.title}</h2>
      <p className="myPosts-description">{post.description}</p>
      {post.cover_image && (
        <img className="myPosts-cover" src={post.cover_image} alt={`${post.title}的配图`} loading="lazy" />
      )}
      {post.tags?.length > 0 && (
        <div className="myPosts-tags">
          {post.tags.map(tag => <Tag key={tag.id}>{tag.name}</Tag>)}
        </div>
      )}
      <div className="myPosts-meta">
        {post.is_recruiting && post.location && <span><EnvironmentOutlined />{post.location}</span>}
        {post.is_recruiting && post.max_participants && <span><TeamOutlined />招募 {post.max_participants} 人</span>}
        {expireAt && <span><ClockCircleOutlined />截止 {expireAt}</span>}
        <span><LikeOutlined />{post.like_count || 0} 点赞</span>
        <span><MessageOutlined />{post.comment_count || 0} 评论</span>
      </div>
      </Link>
      <PostSchoolBinding post={post} />
      {(post.is_recruiting || post.completed_at || post.approved_participant_count > 0 || post.pending_application_count > 0) && <div className="myPosts-manage"><Link to={`/posts/${post.id}/manage`}>管理招募申请{post.pending_application_count > 0 ? `（待审批 ${post.pending_application_count}）` : ''}</Link></div>}
    </article>
  );
};

const MyPosts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filters, setFilters, restored] = useBrowseState('my-posts:filters', {});
  const goBack = usePageBack('/personal');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 480);
  const { isAuthenticated } = useSelector(state => state.user);
  const { myList, myPagination, myStatus, myError, myRequestedPage, myFilters } = useSelector(state => state.posts);
  const resumeQuery = useRef(restored && myStatus === 'succeeded' && JSON.stringify(filters) === JSON.stringify(myFilters) ? JSON.stringify(filters) : null);
  const hasMore = myPagination && myPagination.page < myPagination.totalPages;

  useEffect(() => {
    if (resumeQuery.current === JSON.stringify(filters)) return;
    resumeQuery.current = null;
    if (isAuthenticated) dispatch(loadMyPosts({ page: 1, filters }));
  }, [dispatch, isAuthenticated, filters]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleRefresh = useCallback(() => dispatch(loadMyPosts({ page: 1, filters })).unwrap(), [dispatch, filters]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <div className="myPosts">
      <PageTopbar className="myPosts-mobile-topbar" title="我的帖子" onBack={goBack} />
      <Content>
        <PullToRefresh enabled={isMobile} disabled={myStatus === 'loading'} onRefresh={handleRefresh} successMessage="我的帖子已刷新">
        <section className="myPosts-panel" aria-labelledby="myPosts-title">
          <div className="myPosts-heading">
            <div>
              <h1 id="myPosts-title">我的帖子</h1>
              <p>查看你发布过的全部帖子和当前状态。</p>
            </div>
            <div className="myPosts-heading-actions">
              {myPagination && <span>共 {myPagination.total} 条</span>}
              {!isMobile && <Button icon={<ReloadOutlined />} disabled={myStatus === 'loading'} onClick={() => dispatch(loadMyPosts({ page: 1, filters }))}>
                刷新
              </Button>}
            </div>
          </div>
          <Form initialValues={filters} className="myPosts-filters" layout="inline" onFinish={values => { setFilters(Object.fromEntries(Object.entries(values).filter(([, value]) => value))); }}>
            <Form.Item name="status" label="帖子状态"><Select allowClear placeholder="全部状态" options={[{ value: 'active', label: '活跃' }, { value: 'closed', label: '已关闭' }, { value: 'expired', label: '已过期' }]} /></Form.Item>
            <Form.Item name="completed" label="活动完成"><Select allowClear placeholder="全部活动" options={[{ value: 'true', label: '已完成' }, { value: 'false', label: '未完成' }]} /></Form.Item>
            <Form.Item name="has_pending_applications" label="待审批申请"><Select allowClear placeholder="全部帖子" options={[{ value: 'true', label: '有待审批' }, { value: 'false', label: '无待审批' }]} /></Form.Item>
            <Form.Item><Button type="primary" htmlType="submit" disabled={myStatus === 'loading'}>筛选</Button></Form.Item>
          </Form>

          {(myStatus === 'idle' || myStatus === 'loading') && myList.length === 0 && (
            <div className="myPosts-list" aria-label="正在加载我的帖子">
              {[1, 2].map(item => <div className="myPosts-card" key={item}><Skeleton active paragraph={{ rows: 3 }} /></div>)}
            </div>
          )}

          {myList.length > 0 && (
            <div className="myPosts-list">
              {myList.map(post => <MyPostCard key={post.id} post={post} />)}
            </div>
          )}

          {myStatus === 'failed' && (
            <Alert
              type="error"
              showIcon
              message={myError || '我的帖子加载失败，请稍后重试'}
              action={<Button size="small" onClick={() => dispatch(loadMyPosts({ page: myRequestedPage, filters }))}>重试</Button>}
            />
          )}

          {myStatus === 'succeeded' && myList.length === 0 && (
            <Empty description="你还没有发布帖子">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/posts/create')}>发布搭子招募</Button>
            </Empty>
          )}

          {hasMore && myStatus !== 'failed' && (
            <Button className="myPosts-more" loading={myStatus === 'loading'} onClick={() => dispatch(loadMyPosts({ page: myPagination.page + 1, filters }))}>
              加载更多
            </Button>
          )}
        </section>
        </PullToRefresh>
      </Content>
    </div>
  );
};

export default MyPosts;
